import React from 'react';
import c from 'classnames';
import { SearchkitWrapper } from '@uncinc/react-searchkit';
import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import {
  APP_OPTIONS,
  SEARCHKIT_HOST_URL,
} from '../../config/constants';

import {
  facetAttributes,
  resultAttributes,
  searchAttributes,
  sorting,
} from './config';

// Subcomponents.
import SearchBar from '../SearchBar';
import Results from './Results';
import Facets from './Facets';
import SearchPagination from '../SearchPagination';

interface SearchAllContentSubcomponents {
  SearchBar: typeof SearchBar;
  SearchResults: typeof Results;
  SearchFacets: typeof Facets;
  SearchPagination: typeof SearchPagination;
}

const SEARCH_ITEMS_PER_PAGE = 9;

export type SearchAllContentProps = Omit<SearchkitWrapperProps, 'connection' | 'indexName' | 'searchSettings'>;

// eslint-disable-next-line max-len
const SearchAllContent: React.FC<SearchAllContentProps> & SearchAllContentSubcomponents = (props) => {
  const {
    className,
    children,
    routing = true,
    ...restProps
  } = props;

  const classNames = c('SearchAllContent', className);

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      connection={{ host: SEARCHKIT_HOST_URL }}
      indexName={`${APP_OPTIONS.parameters.searchkitIndexAllMembers}_title`}
      routing={routing}
      hitsPerPage={SEARCH_ITEMS_PER_PAGE}
      searchSettings={{
        search_attributes: searchAttributes,
        result_attributes: resultAttributes,
        facet_attributes: facetAttributes,
        sorting,
      }}
      requestOptions={{
        hooks: {
          beforeSearch: (requests) => {
            requests.forEach((request) => {
              if (!request.request.params?.query) {
                return;
              }

              const { query } = request.request.params;
              const isNumeric = /\d+/.test(query);
              const fuzziness = isNumeric ? 0 : 'AUTO';

              // If the query is numeric, we want to turn off fuzziness. This is so users can search
              // for the exact KVK number and not find any similar matches.
              request.body.query = {
                bool: {
                  should: [
                    {
                      multi_match: {
                        query,
                        fuzziness,
                      },
                    },
                  ],
                },
              };
            });

            return Promise.resolve(requests);
          },
        },
      }}
    >
      {children}
    </SearchkitWrapper>
  );
};

SearchAllContent.SearchBar = SearchBar;
SearchAllContent.SearchResults = Results;
SearchAllContent.SearchFacets = Facets;
SearchAllContent.SearchPagination = SearchPagination;

export default SearchAllContent;
